/**
 * Documentation navigation structure.
 *
 * Each page has a corresponding mdsvex route at:
 * src/routes/docs/{slug}/+page.md
 */

export interface DocPage {
	slug: string;
	title: string;
	description?: string;
}

export interface DocSection {
	id: string;
	title: string;
	pages: DocPage[];
}

export const docsNavigation: DocSection[] = [
	{
		id: 'getting-started',
		title: 'Getting Started',
		pages: [
			{
				slug: 'getting-started/what-is-corral',
				title: 'What is Corral?',
				description: 'What Corral does and how on-tenant deployment works'
			},
			{
				slug: 'getting-started/deploy-from-marketplace',
				title: 'Deploy from Azure Marketplace',
				description: 'Step-by-step Azure Marketplace deployment'
			},
			{
				slug: 'getting-started/first-30-minutes',
				title: 'Your First 30 Minutes',
				description: 'Get productive with Corral right after deployment'
			},
			{
				slug: 'getting-started/concepts',
				title: 'Key Concepts',
				description: 'Core concepts: root projects, projects, apps, and agents'
			}
		]
	},
	{
		id: 'architecture',
		title: 'Architecture',
		pages: [
			{
				slug: 'architecture/how-on-tenant-works',
				title: 'How On-Tenant Works',
				description: 'Azure Managed Applications and the on-tenant model'
			},
			{
				slug: 'architecture/what-gets-deployed',
				title: 'What Gets Deployed',
				description: 'Azure resources provisioned during deployment'
			},
			{
				slug: 'architecture/publisher-access',
				title: 'Publisher Access & Maintenance',
				description: 'How Corral maintains your deployment'
			},
			{
				slug: 'architecture/network-and-data-residency',
				title: 'Network & Data Residency',
				description: 'Data residency, network boundaries, and compliance'
			},
			{
				slug: 'architecture/multi-cloud',
				title: 'Multi-Cloud Status',
				description: 'Azure-first with multi-cloud on the roadmap'
			}
		]
	},
	{
		id: 'building-agents',
		title: 'Building Agents',
		pages: [
			{
				slug: 'building-agents/assistants-and-sub-assistants',
				title: 'Assistants & Sub-Assistants',
				description: 'Agent composition and multi-agent patterns'
			},
			{
				slug: 'building-agents/intelligence-configuration',
				title: 'Intelligence Configuration',
				description: 'System prompts, model selection, and intelligence types'
			},
			{
				slug: 'building-agents/tools-built-in',
				title: 'Tools: Built-in',
				description: 'File management, code execution, Graph, and more'
			},
			{
				slug: 'building-agents/tools-mcp',
				title: 'Tools: MCP Connections',
				description: 'Connect agents to external tools via MCP'
			},
			{
				slug: 'building-agents/tools-openapi',
				title: 'Tools: OpenAPI Connections',
				description: 'Connect agents to APIs via OpenAPI specs'
			},
			{
				slug: 'building-agents/channels',
				title: 'Channels',
				description: 'Deploy agents to Teams, web, widget, and API'
			},
			{
				slug: 'building-agents/publishing-and-versions',
				title: 'Publishing & Versions',
				description: 'Version management and publish workflow'
			}
		]
	},
	{
		id: 'using-corral',
		title: 'Using Corral',
		pages: [
			{
				slug: 'using-corral/the-hub',
				title: 'The Hub',
				description: 'End-user interface for interacting with agents'
			},
			{
				slug: 'using-corral/canvas',
				title: 'The Canvas',
				description: 'Rich content rendering and interactive outputs'
			},
			{
				slug: 'using-corral/admin-console',
				title: 'The Admin Console',
				description: 'Manage agents, users, and settings'
			},
			{
				slug: 'using-corral/embedded-widget',
				title: 'The Embedded Widget',
				description: 'Embed AI chat on any website'
			}
		]
	},
	{
		id: 'security-and-governance',
		title: 'Security & Governance',
		pages: [
			{
				slug: 'security-and-governance/security-architecture',
				title: 'Security Architecture',
				description: 'Security model and trust boundaries'
			},
			{
				slug: 'security-and-governance/cumulative-restrictions',
				title: 'The Cumulative Restrictions Framework',
				description: 'How Corral tracks exposure and restricts actions'
			},
			{
				slug: 'security-and-governance/permissions',
				title: 'Permissions & Access Control',
				description: 'Role-based permissions across root projects and projects'
			},
			{
				slug: 'security-and-governance/authentication',
				title: 'Authentication',
				description: 'Azure AD integration and auth flows'
			},
			{
				slug: 'security-and-governance/audit-and-observability',
				title: 'Audit & Observability',
				description: 'Logging, monitoring, and compliance auditing'
			}
		]
	},
	{
		id: 'extending-corral',
		title: 'Extending Corral',
		pages: [
			{
				slug: 'extending-corral/api-reference',
				title: 'API Reference',
				description: 'REST API and SignalR endpoints'
			},
			{
				slug: 'extending-corral/client-credentials',
				title: 'Client Credentials & OAuth',
				description: 'Service-to-service authentication'
			},
			// {
			// 	slug: 'extending-corral/building-custom-surfaces',
			// 	title: 'Building Custom Surfaces',
			// 	description: 'Build custom UIs on top of Corral'
			// },
			// {
			// 	slug: 'extending-corral/for-partners',
			// 	title: 'For SIs & Partners',
			// 	description: 'Partner integration and white-label options'
			// }
		]
	}
];

function flattenPages(): DocPage[] {
	return docsNavigation.flatMap((section) => section.pages);
}

export function getAdjacentPages(slug: string): { prev?: DocPage; next?: DocPage } {
	const pages = flattenPages();
	const currentIndex = pages.findIndex((page) => page.slug === slug);

	if (currentIndex === -1) {
		return {};
	}

	return {
		prev: currentIndex > 0 ? pages[currentIndex - 1] : undefined,
		next: currentIndex < pages.length - 1 ? pages[currentIndex + 1] : undefined
	};
}

export function getFirstPage(): DocPage | undefined {
	return docsNavigation[0]?.pages[0];
}
